import { Customer, Invoice, InvoiceDatevConfig } from '../types';
import { debtorAccountFor } from './datev/datevExport';
import {
    buildExtfMetaLine,
    escapeExtfValue,
    EXTF_COL,
    EXTF_COLUMNS,
    EXTF_HEADER_LINE,
    extfColumnIndex,
} from './datevExtf';
import { ParsedPersonName, parsePersonName } from './personName';

/**
 * Debitoren-Export im Format der Steuerberater-Liste:
 * Kto-Nr.;Kontenbezeichnung;Straße;PLZ;Ort;Land;Name;Zusatzbezeichnung
 * Semikolon, CRLF, UTF-8 mit BOM (Excel). Ueber den Listen-Upload
 * re-importierbar. Kontenbezeichnung = Name auf 40 Zeichen gekuerzt,
 * Ueberlauf in Zusatzbezeichnung (wie in der Vorlage-Liste).
 */

const HEADERS = ['Kto-Nr.', 'Kontenbezeichnung', 'Straße', 'PLZ', 'Ort', 'Land', 'Name', 'Zusatzbezeichnung'];
const NAME_MAX = 40;

const esc = (v: string): string =>
    /[;"\r\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v;

/** Nummern numerisch, nummernlose Kunden zuletzt nach Name. */
const byCustomerNumber = (a: Customer, b: Customer): number => {
    const na = Number.parseInt(a.customerNumber ?? '', 10);
    const nb = Number.parseInt(b.customerNumber ?? '', 10);
    const va = Number.isFinite(na);
    const vb = Number.isFinite(nb);
    if (va && vb) return na - nb;
    if (va) return -1;
    if (vb) return 1;
    return a.name.localeCompare(b.name, 'de');
};

export const exportCustomersDatevCsv = (customers: Customer[]): string => {
    const sorted = [...customers].sort(byCustomerNumber);
    const out = [HEADERS.join(';')];
    for (const c of sorted) {
        const name = (c.name ?? '').trim();
        out.push([
            c.customerNumber ?? '',
            name.slice(0, NAME_MAX),
            c.street ?? '',
            c.zip ?? '',
            c.city ?? '',
            'DE',
            name,
            name.length > NAME_MAX ? name.slice(NAME_MAX).trim() : '',
        ].map(esc).join(';'));
    }
    return '\ufeff' + out.join('\r\n') + '\r\n';
};

/**
 * Debitorenkonto eines Kunden \u2014 DIESELBE Ableitung wie im Buchungsstapel.
 *
 * Nachgebaut waere sie die zweite Fassung derselben Regel: ist ein Praefix
 * (debtorAccountBase) gepflegt, buchte der Stapel auf 120030806, waehrend der
 * Stamm 30806 beschriftete. Die Kanzlei bekaeme dann Namen zu Konten, die in
 * keiner einzigen Buchung vorkommen \u2014 und zu den gebuchten Konten keinen
 * Namen. Deshalb ruft der Stamm die Ableitung des Stapels auf
 * (datev/datevExport.ts).
 *
 * debtorAccountFor liest aus dem Beleg allein den Kundenschnappschuss; der
 * Kunde wird ihm darum als ebendieser untergeschoben, statt die Funktion fuer
 * diesen Aufruf umzubauen.
 */
const debtorAccountOf = (customer: Customer, config: InvoiceDatevConfig): string =>
    debtorAccountFor({ id: customer.id, customerSnapshot: customer } as Invoice, config);

/** Ein Kunde, der nicht in den Stamm konnte \u2014 mit dem Grund im Klartext. */
export interface DatevExtfExportOptions {
    /**
     * Wird je ausgelassenem Kunden gerufen. Ohne diese Meldung waere das
     * Auslassen still, und eine fehlende Kontenbeschriftung faellt in der
     * Kanzlei erst beim Kontenabgleich auf.
     */
    onSkipped?: (message: string) => void;
}

/**
 * Debitoren-Export im DATEV-EXTF-Format "Debitoren/Kreditoren" \u2014 das Format,
 * in dem die Kanzlei ihre eigene Liste liefert (services/datevExtf.ts).
 *
 * Zwei harte Regeln:
 *  - JEDE Datenzeile hat exakt so viele Felder wie die Kopfzeile. Die Zeile
 *    entsteht darum als Array aus Leerstrings ueber die Kopf-Laenge und wird
 *    ausschliesslich ueber SPALTENNAMEN befuellt.
 *  - Wir exportieren SAUBER, auch wenn die Quelldatei der Kanzlei es nicht tut:
 *    Unternehmen bekommen Adressattyp 2 und stehen in der Unternehmens-Spalte,
 *    Privatpersonen Adressattyp 1 mit getrenntem Nachnamen und Vornamen.
 *
 * Steuerungs- und Mahnwesen-Spalten (Zahlungsbedingung, Mahnstufen, Fristen,
 * Bankverbindungen) bleiben LEER \u2014 dort haengen Buchungsautomatiken, deren
 * Bedeutung nur die Kanzlei kennt. Sie zu raten waere schlimmer als sie
 * wegzulassen.
 *
 * Die DATEV-Konfiguration wird nur durchgereicht: Berater-, Mandantennummer,
 * Wirtschaftsjahresbeginn und Sachkontenlaenge gehoeren in die Kopfzeile
 * (buildExtfMetaLine). Fehlt sie, wirft der Export — der Debitorenstamm muss
 * dieselben Mandantenangaben tragen wie der Buchungsstapel, sonst ordnet die
 * Kanzlei die beiden Dateien nicht zusammen.
 *
 * Ein Kunde, dessen Nummer kein gueltiges Debitorenkonto ergibt, wird
 * AUSGELASSEN und ueber onSkipped gemeldet, statt den ganzen Stamm abzubrechen.
 * Der laute Abbruch waere hier die schlechtere Wahl: derselbe Kunde bringt den
 * BUCHUNGSSTAPEL ohnehin zum Abbruch (dieselbe Ableitung, derselbe Wurf), und
 * zwar bevor der Stamm ueberhaupt entsteht — im Paket kann also gar kein Konto
 * fehlen, das im Stapel vorkommt. Im CRM dagegen, wo der gesamte Kundenstamm
 * exportiert wird, duerfte eine einzelne unbrauchbare Nummer nicht die Liste
 * aller uebrigen Kunden verhindern.
 */
export const exportCustomersDatevExtf = (
    customers: Customer[],
    config: InvoiceDatevConfig | undefined,
    now: Date = new Date(),
    options: DatevExtfExportOptions = {},
): string => {
    const columnIndex = new Map<string, number>();
    for (const key of Object.values(EXTF_COL)) {
        columnIndex.set(key, extfColumnIndex(EXTF_COLUMNS, key));
    }
    const out = [buildExtfMetaLine(config, now), EXTF_HEADER_LINE];
    // buildExtfMetaLine hat ohne Konfiguration schon geworfen; dem Typsystem
    // sagt das niemand, deshalb hier die eine Zusicherung statt einer zweiten
    // Pruefung mit einer zweiten Fehlermeldung.
    const datevConfig = config as InvoiceDatevConfig;

    for (const c of [...customers].sort(byCustomerNumber)) {
        let account: string;
        try {
            account = debtorAccountOf(c, datevConfig);
        } catch {
            options.onSkipped?.(
                `${(c.name ?? '').trim() || c.id} (Kundennummer ` +
                    `„${(c.customerNumber ?? '').trim() || '—'}“): kein gültiges ` +
                    'Debitorenkonto — nicht im Debitorenstamm. Ein Beleg dieses Kunden ' +
                    'würde auch im Buchungsstapel abbrechen.',
            );
            continue;
        }
        const fields = new Array<string>(EXTF_COLUMNS.length).fill('');
        const put = (column: string, value: string | undefined) => {
            const i = columnIndex.get(column);
            if (i === undefined || i < 0 || !value) return;
            fields[i] = value;
        };
        const name = (c.name ?? '').trim();
        const isPerson = c.customerType === 'person';

        put(EXTF_COL.account, account);
        if (isPerson) {
            // Ohne gepflegte Teile wird der Vollname zerlegt \u2014 dieselbe Regel,
            // die die Kundenmaske anzeigt (services/personName.ts).
            const first = (c.firstName ?? '').trim();
            const last = (c.lastName ?? '').trim();
            const parsed: ParsedPersonName =
                first || last ? { firstName: first, lastName: last } : parsePersonName(name);
            put(EXTF_COL.addressType, '1');
            put(EXTF_COL.personLastName, parsed.lastName || name);
            put(EXTF_COL.personFirstName, parsed.firstName);
            put(EXTF_COL.salutation, (c.salutation ?? '').trim() || parsed.salutation);
            put(EXTF_COL.title, (c.title ?? '').trim() || parsed.title);
        } else {
            put(EXTF_COL.addressType, '2');
            put(EXTF_COL.companyName, name);
        }
        // Kurzbezeichnung bleibt LEER — so von der Kanzlei gewuenscht.
        //
        // DATEV fuehrt sie als eigenes Suchfeld mit 15 Zeichen. Der daraus
        // gekuerzte Firmenname ist bei aehnlich beginnenden Namen nicht
        // unterscheidbar ("Landesbetrieb S..."), und die Kanzlei vergibt das
        // Kuerzel lieber selbst nach ihrer eigenen Systematik. Ein von uns
        // gesetzter Wert wuerde ihres bei jedem Re-Import ueberschreiben.
        put(EXTF_COL.addressKind, 'STR');
        put(EXTF_COL.street, (c.street ?? '').trim());
        put(EXTF_COL.zip, (c.zip ?? '').trim());
        put(EXTF_COL.city, (c.city ?? '').trim());
        put(EXTF_COL.country, 'DE');
        put(EXTF_COL.phone, (c.phone ?? '').trim());
        put(EXTF_COL.email, (c.email ?? '').trim());
        put(EXTF_COL.language, '1');

        out.push(fields.map(escapeExtfValue).join(';'));
    }
    return '\ufeff' + out.join('\r\n') + '\r\n';
};
